import { useMemo } from "react";
import { useAuthContext } from "../context/AuthContext/AuthContext";
import {
  hasPriceCategoryAccess,
  shouldShowPriceForCategories,
} from "../helpers/getCategoryDescendants";
import * as shopQueries from "../../queries/shop";
import { usePersistedQuery } from "./usePersistedQuery";
import { QUERY_TTL } from "../helpers/persistedQueryCache";

interface OrderPriceItem {
  product?: {
    productCategories?: {
      nodes?: {
        databaseId: number;
      }[];
    };
  };
}

interface OrderPriceVisibilityOptions {
  showWhenNoPriceCategory?: boolean;
}

export const useOrderPriceVisibility = (
  orderList: OrderPriceItem[],
  options?: OrderPriceVisibilityOptions
) => {
  const authCtx = useAuthContext();
  const showPriceCategories = authCtx.user?.showPriceCategories;
  const allowedCategories = Array.isArray(showPriceCategories)
    ? showPriceCategories
    : [];
  const hasPriceAccess =
    authCtx.isAuthenticated &&
    !!authCtx.user &&
    authCtx.user.showPrice !== false &&
    hasPriceCategoryAccess(showPriceCategories);
  const showWithoutCategoryAccess = !!options?.showWhenNoPriceCategory;
  const needsCategoryTree = hasPriceAccess && allowedCategories.length > 0;

  const { data: categoryData } = usePersistedQuery<
    shopQueries.GetAllProductCategoriesResponse
  >(shopQueries.getAllProductCategories, {
    skip: !needsCategoryTree,
    persistKey: "allProductCategories",
    persistTtl: QUERY_TTL.allCategories,
  });

  const itemVisibility = useMemo(() => {
    return orderList.map((item) => {
      const productCategoryIds =
        item.product?.productCategories?.nodes
          ?.filter((cat) => cat && cat.databaseId != null)
          .map((cat) => cat.databaseId) || [];

      if (!hasPriceAccess) {
        return showWithoutCategoryAccess;
      }

      if (!needsCategoryTree) {
        return false;
      }

      if (!categoryData?.productCategories?.nodes) {
        return false;
      }

      return shouldShowPriceForCategories(
        productCategoryIds,
        allowedCategories,
        categoryData.productCategories.nodes
      );
    });
  }, [
    orderList,
    hasPriceAccess,
    showWithoutCategoryAccess,
    needsCategoryTree,
    allowedCategories,
    categoryData?.productCategories?.nodes,
  ]);

  const canShowTotals =
    (hasPriceAccess || showWithoutCategoryAccess) &&
    itemVisibility.length > 0 &&
    itemVisibility.every(Boolean);

  return {
    itemVisibility,
    canShowTotals,
  };
};
